import { z } from 'zod';
import { tryCatch } from '~/utils/TryCatch';

export type TLikelyToFillProfession = 'RDH' | 'DN' | 'DA' | 'EFDA' | 'FD';

/** The lowest whole-dollar gap (posted rate minus the per-office average rate) for each band. */
export interface ILikelyToFillLimits {
  readonly veryLikely: number;
  readonly likely: number;
  readonly lessLikely: number;
  readonly warning: number;
}

export type TLikelyToFillBands = Readonly<Record<TLikelyToFillProfession, ILikelyToFillLimits>>;

const HYGIENIST_DENTIST_LIMITS: ILikelyToFillLimits = {
  veryLikely: 5,
  likely: 0,
  lessLikely: -3,
  warning: -6,
};

const ASSISTANT_FRONT_DESK_LIMITS: ILikelyToFillLimits = {
  veryLikely: 3,
  likely: 0,
  lessLikely: -2,
  warning: -5,
};

/** Identical to the `treatment` payload of the GOTU-13065 flag; used when it's missing or invalid. */
export const DEFAULT_LIKELY_TO_FILL_BANDS: TLikelyToFillBands = {
  RDH: HYGIENIST_DENTIST_LIMITS,
  DN: HYGIENIST_DENTIST_LIMITS,
  DA: ASSISTANT_FRONT_DESK_LIMITS,
  EFDA: ASSISTANT_FRONT_DESK_LIMITS,
  FD: ASSISTANT_FRONT_DESK_LIMITS,
};

const LIKELY_TO_FILL_PROFESSIONS = Object.keys(
  DEFAULT_LIKELY_TO_FILL_BANDS,
) as TLikelyToFillProfession[];

export const isLikelyToFillProfession = (code: unknown): code is TLikelyToFillProfession =>
  typeof code === 'string' && (LIKELY_TO_FILL_PROFESSIONS as string[]).includes(code);

// Strictly descending integers with lessLikely ≤ 0, so the bands can't overlap and the "$X+" copy
// stays a positive whole-dollar amount.
const limitsSchema = z
  .object({
    veryLikely: z.number().int(),
    likely: z.number().int(),
    lessLikely: z.number().int(),
    warning: z.number().int(),
  })
  .refine(
    ({ veryLikely, likely, lessLikely, warning }) =>
      veryLikely > likely && likely > lessLikely && lessLikely > warning && lessLikely <= 0,
  );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toObject = (payload: unknown): unknown => {
  if (typeof payload !== 'string') return payload;
  const parsed = tryCatch(() => JSON.parse(payload) as unknown);
  return parsed.ok ? parsed.data : null;
};

// Each profession is validated on its own, so one bad entry doesn't drop the others.
export const parseLikelyToFillBands = (payload: unknown): TLikelyToFillBands => {
  const value = toObject(payload);
  if (!isRecord(value)) return DEFAULT_LIKELY_TO_FILL_BANDS;

  return LIKELY_TO_FILL_PROFESSIONS.reduce<Record<TLikelyToFillProfession, ILikelyToFillLimits>>(
    (bands, profession) => {
      const result = limitsSchema.safeParse(value[profession]);
      bands[profession] = result.success ? result.data : DEFAULT_LIKELY_TO_FILL_BANDS[profession];
      return bands;
    },
    { ...DEFAULT_LIKELY_TO_FILL_BANDS },
  );
};
